import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import TableCell from '@material-ui/core/TableCell'
import TableBody from '@material-ui/core/TableBody'
import Tooltip from '@material-ui/core/Tooltip'
import Chip from '@material-ui/core/Chip'
import { useTranslation } from 'react-i18next'
import {
  IParkingLotSessionNotificationsNotificationData,
  ParkingLotSessionNotificationStatus,
  useApi,
} from 'components/providers/ApiProvider'
import {
  ListSectionBody,
  ListSectionDatetime,
  ListSectionIdCell,
  ListSectionSkeletonRow,
  orDash,
} from 'components/views/parking-lot/elements/ListSection'

type Props = {
  session: {
    id: number,
  }
  expand: boolean
}
type Functions = {}
type AllProps = Props & Functions

const ListSessionNotifications: FunctionComponent<AllProps> = ({ session, expand }) => {
  if (!session) {
    return null
  }

  const { t } = useTranslation()
  const api = useApi()

  const [error, setError] = useState<{ status?: number }>(null)
  const [notifications, setNotifications] = useState<IParkingLotSessionNotificationsNotificationData[]>(null)

  const loaded = useRef(false)

  const load = () => {
    api.getParkingLotSessionNotifications({ session_id: session.id })
      .then(data => {
        setNotifications(data.notifications)
      })
      .catch(error => {
        setError(error || {})
      })
  }

  useEffect(() => {
    if (!expand || loaded.current) {
      return
    }

    loaded.current = true

    load()
  }, [expand])

  const errorMessage = error && (error.status == 403 ? t('noAccess') : `${t('alerts.couldNotLoadData')} ${t('alerts.tryLater')}`)

  return (
    <ListSectionBody expand={expand} error={errorMessage} empty={Boolean(notifications && !notifications.length)}>
      <TableHead>
        {!notifications && <ListSectionSkeletonRow />}
        {notifications && (
          <TableRow>
            <TableCell>{t('createdAt')}</TableCell>
            <TableCell>{t('type')}</TableCell>
            <TableCell>{t('mobillyClientId')}</TableCell>
            <TableCell>{t('channel')}</TableCell>
            <TableCell>{t('status')}</TableCell>
            <TableCell />
          </TableRow>
        )}
      </TableHead>
      <TableBody>
        {!notifications && <ListSectionSkeletonRow />}
        {notifications && notifications.map((notification, key) => {
          const type = notification.type && t(`notificationTypes.${notification.type}`, { defaultValue: notification.type })

          return <TableRow key={key}>
            <TableCell><ListSectionDatetime value={notification.create_time} /></TableCell>
            <TableCell>
              <Tooltip title={type && type != notification.type ? notification.type : ''} placement={'top'} arrow interactive>
                <span>{orDash(type)}</span>
              </Tooltip>
            </TableCell>
            <TableCell>{orDash(notification.mobilly_client_id)}</TableCell>
            <TableCell>{orDash(notification.channel && t(`notificationChannels.${notification.channel}`, { defaultValue: notification.channel }))}</TableCell>
            <TableCell style={{ paddingTop: 4, paddingBottom: 4 }}>
              <Chip
                size="small"
                variant="outlined"
                label={t(`notificationStatuses.${notification.status}`)}
                style={notification.status == ParkingLotSessionNotificationStatus.Created
                  ? { color: '#ff9800', borderColor: '#ff9800' }
                  : undefined}
              />
            </TableCell>
            <ListSectionIdCell id={notification.id} />
          </TableRow>
        })}
      </TableBody>
    </ListSectionBody>
  )
}

export default ListSessionNotifications
